import { Link } from 'react-router-dom';
import cx from 'classnames';

import { ArrowRightIcon, Badge } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';
import { BUTTON_AS_TYPE, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';
import { filterDataProps } from '@motorway/mw-highway-code/src/utils/helpers';

import { ClickableCardProps } from './ClickableCard.types';

import styles from './ClickableCard.module.scss';

export type ClickableCardWrapperProps = {
  children: React.ReactNode;
  className: string;
  onClick?: () => void;
  rel?: string;
  route: string;
  target: string;
};

const isExternalLink = (r: string) => /^https?:\/\//.test(r);

const ClickableCardWrapper = ({ children, className, onClick, rel = 'noreferrer noopener', route, target, ...rest }: ClickableCardWrapperProps) => (<>
  {isExternalLink(route)
    ? (
      <a {...{ className, onClick, rel, target }} href={route} {...filterDataProps(rest)}>
        {children}
      </a>
    ) : (
      <Link {...{ className, onClick, rel, target }} to={route} {...filterDataProps(rest)}>
        {children}
      </Link>
    )
  }
</>);

export const ClickableCard = ({ badgeText, img, link, onClick, theme = 'white', title, ...props }: ClickableCardProps) => {
  const { ctaLabel, openInNewTab = false, route } = link;

  return (
    <ClickableCardWrapper
      className={cx(styles.component, styles.card, styles[theme])}
      target={openInNewTab ? '_blank' : '_self'}
      {...{ onClick, route, ...props }}
    >
      {img && (
        <picture className={styles.inlineImage}>
          {img.src?.avif && <source srcSet={img?.src.avif} type="image/avif" />}
          {img.src?.webp && <source srcSet={img?.src.webp} type="image/webp" />}
          <img
            alt={img?.alt}
            height={150}
            loading='lazy'
            src={img.src.png}
            width={150} />
        </picture>
      )}

      <section className={cx(styles.content)}>
        <h4 className={styles.title}>
          <span>{title}</span>
          {badgeText ? <Badge data-thc-badge text={badgeText} variant="primary" /> : null}
        </h4>
        <div className={styles.hyperlink}>
          <Hyperlink
            data-thc-hyperlink
            as={BUTTON_AS_TYPE.BUTTON}
            label={ctaLabel}
            size={SIZE.MEDIUM}
            variant={VARIANT.SECONDARY}
          />
          <ArrowRightIcon size={SIZE.XSMALL} />
        </div>
      </section>
    </ClickableCardWrapper>
  );
};
